import React, { useMemo } from 'react';

function buildPageItems(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 1) return [];
  const pages = new Set<number>();
  pages.add(1);
  pages.add(totalPages);
  for (let d = -1; d <= 1; d++) {
    const p = currentPage + d;
    if (p >= 1 && p <= totalPages) pages.add(p);
  }
  const sorted = Array.from(pages).sort((a, b) => a - b);
  const out: (number | 'ellipsis')[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push('ellipsis');
    out.push(sorted[i]);
  }
  return out;
}

interface ProceedingsPaginationProps {
  currentPage: number;
  totalPages: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  onPageChange: (page: number) => void;
  pageSize?: number;
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (size: number) => void;
}

export const ProceedingsPagination: React.FC<ProceedingsPaginationProps> = ({
  currentPage,
  totalPages,
  hasPrevPage,
  hasNextPage,
  onPageChange,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
}) => {
  const items = useMemo(
    () => buildPageItems(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const showPageSize =
    pageSize !== undefined && pageSizeOptions && pageSizeOptions.length > 0 && onPageSizeChange;

  if (totalPages <= 1 && !showPageSize) return null;

  return (
    <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row sm:flex-wrap sm:gap-x-2 sm:gap-y-3">
      {showPageSize && (
        <label className="flex items-center gap-2 text-[13px] text-neutral-600">
          <span className="font-light">Per page</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="cursor-pointer rounded-md border border-cg-border bg-white py-1.5 pl-2 pr-7 font-sans text-[13px] text-cg-ink outline-none hover:border-neutral-400"
            aria-label="Results per page"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      )}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1.5 sm:gap-2">
          <button
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!hasPrevPage}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-cg-border bg-white text-[13px] text-neutral-500 transition-colors hover:border-neutral-400 hover:text-cg-ink disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Previous page"
          >
            ←
          </button>
          {items.map((item, i) =>
            item === 'ellipsis' ? (
              <span key={`e-${i}`} className="px-1 text-[13px] text-neutral-300">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => onPageChange(item)}
                className={`flex h-8 min-w-[2rem] items-center justify-center rounded-md border px-1.5 text-[13px] transition-colors ${
                  currentPage === item
                    ? 'border-cg-nav bg-cg-nav text-white'
                    : 'border-cg-border bg-white text-neutral-500 hover:border-neutral-400 hover:text-cg-ink'
                }`}
              >
                {item}
              </button>
            )
          )}
          <button
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!hasNextPage}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-cg-border bg-white text-[13px] text-neutral-500 transition-colors hover:border-neutral-400 hover:text-cg-ink disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Next page"
          >
            →
          </button>
        </div>
      )}
    </div>
  );
};
